import { create } from '@bufbuild/protobuf';

import { mapCallFinishReason } from 'APP/model/mainCall/mainCallModel.mapper';
import { mapStickerType } from 'APP/model/stickers/stickersModel.b2b.mapper';
import {
  AlbumPayload,
  AudioPayload,
  E2eePayload,
  FilePayload,
  ForwardedSource,
  ImagePayload,
  Message,
  Message_DeleteMode,
  Message_Direction,
  Message_MediaType,
  MessageCountData,
  MessagePayload,
  OrderedItemPayload,
  QuotedMessageData,
  QuotedMessageData_Reply,
  QuotedMessageData_Text,
  ScheduledMessage,
  Source,
  Source_Chat,
  Source_Unknown,
  Source_User,
  Source_UserChat,
  StickerPayload,
  TextPayload,
  UnsupportedPayload,
  VideoPayload,
  VoicePayload,
} from 'APP/wsApi/wsAppApi/types/message/message_model_pb';
import {
  QuotedMessageRequestData,
  QuotedMessageRequestDataSchema,
  ScheduleMessageRequest,
  ScheduleMessageRequestSchema,
  SendMessagePayloadData,
  SendMessagePayloadData_AlbumData,
  SendMessagePayloadData_AlbumDataSchema,
  SendMessagePayloadData_AudioData,
  SendMessagePayloadData_AudioDataSchema,
  SendMessagePayloadData_E2eeData,
  SendMessagePayloadData_E2eeDataSchema,
  SendMessagePayloadData_FileData,
  SendMessagePayloadData_FileDataSchema,
  SendMessagePayloadData_ImageData,
  SendMessagePayloadData_ImageDataSchema,
  SendMessagePayloadData_OrderedItemData,
  SendMessagePayloadData_OrderedItemDataSchema,
  SendMessagePayloadData_StickerData,
  SendMessagePayloadData_StickerDataSchema,
  SendMessagePayloadData_TextData,
  SendMessagePayloadData_TextDataSchema,
  SendMessagePayloadData_VideoData,
  SendMessagePayloadData_VideoDataSchema,
  SendMessagePayloadData_VoiceData,
  SendMessagePayloadData_VoiceDataSchema,
  SendMessagePayloadDataSchema,
  UpdateScheduledMessageRequest,
  UpdateScheduledMessageRequestSchema,
} from 'APP/wsApi/wsAppApi/types/message/message_requests_pb';
import {
  ForwardMessagesResponse,
  GetScheduledMessagesSuccess,
} from 'APP/wsApi/wsAppApi/types/message/message_responses_pb';
import {
  SystemMessage,
  SystemMessage_CallPayload,
  SystemMessage_ChatTtlChangedPayload,
  SystemMessage_ChatTypeChangedPayload,
  SystemMessage_ChatTypeChangedPayload_Type,
  SystemMessage_SystemMessagePayload,
  SystemMessage_UsersChangedByPayload,
  SystemMessage_UsersChangedPayload,
} from 'APP/wsApi/wsAppApi/types/message/message_system_model_pb';

import {
  mapILinkPreviewModel,
  mapToGraphicSize,
  mapToIGraphicSize,
  mapToLinkPreview,
  mapUserMention,
} from '../common/commonModel.b2b.mapper';
import { GroupType } from '../group/groupModel.types';
import {
  DeleteMessagesMode,
  GetMessagesDirection,
  IAlbumMessagePayload,
  IAlbumRequestData,
  IAudioMessagePayload,
  IAudioRequestData,
  IE2eeMessagePayload,
  IE2eeRequestData,
  IFileMessagePayload,
  IFileRequestData,
  IForwardMessage,
  IForwardMessagesResponse,
  IGetMessagesByMediaTypesCountResponse,
  IGetScheduledMessagesResponse,
  IImageMessagePayload,
  IImageRequestData,
  IMessage,
  IMessagePayload,
  IMessageRequestQuotedMessageData,
  IOrderedItemMessagePayload,
  IOrderedItemRequestData,
  IQuote,
  IQuoteChatSource,
  IQuoteUnknownSource,
  IQuoteUserChatSource,
  IQuoteUserSource,
  IRepliedMessageData,
  IScheduledMessage,
  ISendScheduledMessageRequest,
  IStickerMessagePayload,
  IStickerRequestData,
  ISystemMessageCallPayload,
  ISystemMessageChatTtlChangedPayload,
  ISystemMessageChatTypeChangedPayload,
  ISystemMessagePayload,
  ISystemMessageUsersChangedPayload,
  ITextMessagePayload,
  ITextRequestData,
  IUnsupportedPayload,
  IUpdateMessageRequestPayloadData,
  IUpdateScheduledMessageRequest,
  IVideoMessagePayload,
  IVideoRequestData,
  IVoiceMessagePayload,
  IVoiceRequestData,
  MediaType,
  PayloadType,
  TQuoteSource,
  VoiceTranscriptionStatus,
} from './messageModel.types';

function mapOrderedItemsMessagePayload(payload: OrderedItemPayload): IOrderedItemMessagePayload {
  if (!payload.payload) {
    throw new Error(`Payload doesn't exist in ordered item with id: ${payload.id}`);
  }

  return {
    payloadType: PayloadType.ContainerOrdered,
    id: payload.id,
    order: payload.order,
    payload: mapIMessagePayload(payload.payload),
  };
}

function mapAlbumMessagePayload(payload: AlbumPayload): IAlbumMessagePayload {
  return {
    payloadType: PayloadType.Album,
    comment: payload.comment,
    userMentions: payload.userMentions.map((item) => mapUserMention(item)),
    payloads: payload.payloads.map((item) => mapOrderedItemsMessagePayload(item)),
  };
}

function mapAudioMessagePayload(payload: AudioPayload): IAudioMessagePayload {
  return {
    payloadType: PayloadType.AudioMessage,
    duration: payload.duration.toString(),
    fileName: payload.fileData?.fileName || '',
    comment: payload.comment || '',
    title: payload.title,
    url: payload.fileData?.url || '',
    artist: payload.artist,
    fileSize: payload.fileData?.fileSize.toString() || '0',
    mimeType: payload.fileData?.mimeType || '',
    userMentions: payload.userMentions.map((item) => mapUserMention(item)),
  };
}

function mapFileMessagePayload(payload: FilePayload): IFileMessagePayload {
  return {
    payloadType: PayloadType.File,
    fileName: payload.fileData?.fileName || '',
    fileSize: payload.fileData?.fileSize.toString() || '0',
    url: payload.fileData?.url || '',
    mimeType: payload.fileData?.mimeType || '',
    comment: payload.comment,
    userMentions: payload.userMentions.map((item) => mapUserMention(item)),
  };
}

function mapImageMessagePayload(payload: ImagePayload): IImageMessagePayload {
  return {
    payloadType: PayloadType.Image,
    fileName: payload.fileData?.fileName || '',
    fileSize: payload.fileData?.fileSize.toString() || '0',
    url: payload.fileData?.url || '',
    mimeType: payload.fileData?.mimeType || '',
    stretchedByWidth: payload.stretchedByWidth,
    graphicSize: mapToIGraphicSize(payload.graphicSize),
    comment: payload.comment,
    userMentions: payload.userMentions.map((item) => mapUserMention(item)),
  };
}

function mapStickerMessagePayload(payload: StickerPayload): IStickerMessagePayload {
  return {
    payloadType: PayloadType.Sticker,
    id: payload.id.toString(),
    type: mapStickerType(payload.type),
    name: payload.name,
    stickerPackId: payload.stickerPackId?.toString(),
    stickerUrl: payload.stickerUrl,
    animationUrl: payload.animationUrl,
    graphicSize: mapToIGraphicSize(payload.graphicSize),
  };
}

function mapTextMessagePayload(payload: TextPayload): ITextMessagePayload {
  return {
    payloadType: PayloadType.RichText,
    text: payload.text,
    linkPreview: payload.linkPreview ? mapILinkPreviewModel(payload.linkPreview) : undefined,
    userMentions: payload.userMentions.map((item) => mapUserMention(item)),
  };
}

function mapE2eeMessagePayload(payload: E2eePayload): IE2eeMessagePayload {
  return {
    payloadType: PayloadType.E2ee,
    selfPayload: payload.selfPayload
      ? payload.selfPayload.buffer.slice(
          payload.selfPayload.byteOffset,
          payload.selfPayload.byteOffset + payload.selfPayload.byteLength
        )
      : undefined,
    e2eePayload: payload.payload.buffer.slice(
      payload.payload.byteOffset,
      payload.payload.byteOffset + payload.payload.byteLength
    ),
    epoch: Number(payload.epoch),
    senderDeviceId: payload.senderDeviceId,
  };
}

function mapVideoMessagePayload(payload: VideoPayload): IVideoMessagePayload {
  return {
    payloadType: PayloadType.Video,
    comment: payload.comment,
    fileName: payload.fileData?.fileName || '',
    fileSize: payload.fileData?.fileSize.toString() || '0',
    url: payload.fileData?.url || '',
    mimeType: payload.fileData?.mimeType || '',
    previewUrl: payload.previewUrl,
    graphicSize: mapToIGraphicSize(payload.graphicSize),
    userMentions: payload.userMentions.map((item) => mapUserMention(item)),
  };
}

function mapVoiceMessagePayload(payload: VoicePayload): IVoiceMessagePayload {
  return {
    payloadType: PayloadType.VoiceMessage,
    comment: payload.comment,
    fileName: payload.fileData?.fileName || '',
    fileSize: payload.fileData?.fileSize.toString() || '0',
    url: payload.fileData?.url || '',
    mimeType: payload.fileData?.mimeType || '',
    duration: payload.duration.toString(),
    histogram: payload.histogram,
    transcriptionText: undefined,
    transcriptionStatus: VoiceTranscriptionStatus.NotTranscribed,
    userMentions: payload.userMentions.map((item) => mapUserMention(item)),
  };
}

function mapSystemMessageDefaultPayload(
  payloadType: PayloadType,
  payload: { payload?: Partial<Omit<SystemMessage_SystemMessagePayload, '$unknown' | '$typeName'>> }
): ISystemMessagePayload {
  return {
    payloadType,
    initiator: payload.payload?.initiatorId?.toString() || '',
    initiatorNickName: payload.payload?.initiatorNickname,
  };
}

function mapSystemMessageCallPayload(
  payload: SystemMessage_CallPayload
): ISystemMessageCallPayload {
  return {
    payloadType: PayloadType.SystemCall,
    initiator: payload.initiatorId.toString(),
    initiatorNickName: payload.initiatorNickname,
    duration: payload.duration?.toString(),
    channelId: payload.channelId,
    finishInitiator: payload.finishInitiatorId?.toString(),
    finishInitiatorNickname: payload.finishInitiatorNickname,
    finishReason:
      payload.finishReason !== undefined ? mapCallFinishReason(payload.finishReason) : undefined,
    finishTs: payload.finishTs?.toString(),
    startTs: payload.startTs?.toString(),
  };
}

function mapSystemMessageChatTypeChangedPayload(
  payload: SystemMessage_ChatTypeChangedPayload
): ISystemMessageChatTypeChangedPayload {
  let newType = GroupType.Closed;
  switch (payload.newType) {
    case SystemMessage_ChatTypeChangedPayload_Type.CLOSED_CHANNEL:
      newType = GroupType.ChannelClosed;
      break;
    case SystemMessage_ChatTypeChangedPayload_Type.OPEN_CHANNEL:
      newType = GroupType.ChannelOpen;
      break;
  }

  return {
    ...mapSystemMessageDefaultPayload(PayloadType.SystemChatTypeChanged, payload),
    newType,
  };
}

function mapSystemMessageUsersChangedPayload(
  payloadType: PayloadType,
  payload?: SystemMessage_UsersChangedByPayload | SystemMessage_UsersChangedPayload
): ISystemMessageUsersChangedPayload {
  return {
    payloadType,
    initiator: (payload as SystemMessage_UsersChangedByPayload)?.initiatorId?.toString() || '',
    initiatorNickName: (payload as SystemMessage_UsersChangedByPayload)?.initiatorNickname,
    users:
      payload?.users.map((user) => ({
        id: user.id.toString(),
        displayedName: user.displayedName,
      })) || [],
  };
}

function mapSystemMessageChatTtlChangedPayload(
  payload?: SystemMessage_ChatTtlChangedPayload
): ISystemMessageChatTtlChangedPayload {
  return {
    payloadType: PayloadType.SystemChatTtlChanged,
    initiator: payload?.initiatorId?.toString() || '',
    initiatorNickName: payload?.initiatorNickname,
    periodMs: payload?.periodMs ? Number(payload?.periodMs) : 0,
  };
}

function mapSystemMessagePayload(payload: SystemMessage): ISystemMessagePayload {
  switch (payload.type.case) {
    case 'call':
      return mapSystemMessageCallPayload(payload.type.value);
    case 'chatTypeChanged':
      return mapSystemMessageChatTypeChangedPayload(payload.type.value);
    case 'chatAdminsAdded':
      return mapSystemMessageUsersChangedPayload(
        PayloadType.SystemChatAdminsAdded,
        payload.type.value.payload
      );
    case 'chatAdminsRemoved':
      return mapSystemMessageUsersChangedPayload(
        PayloadType.SystemChatAdminsRemoved,
        payload.type.value.payload
      );
    case 'chatJoined':
      return mapSystemMessageUsersChangedPayload(
        PayloadType.SystemChatJoined,
        payload.type.value.payload
      );
    case 'chatLeft':
      return mapSystemMessageDefaultPayload(PayloadType.SystemChatLeft, payload.type.value);
    case 'chatPinnedMessage':
      return mapSystemMessageDefaultPayload(
        PayloadType.SystemChatMessagePinned,
        payload.type.value
      );
    case 'chatOwnerChanged':
      return mapSystemMessageUsersChangedPayload(
        PayloadType.SystemChatOwnerChanged,
        payload.type.value.payload
      );
    case 'chatAdded':
      return mapSystemMessageUsersChangedPayload(
        PayloadType.SystemChatAdded,
        payload.type.value.payload
      );
    case 'chatTtlChanged':
      return mapSystemMessageChatTtlChangedPayload(payload.type.value);
    default:
      throw new Error(`Unknown system message payload: ${payload.type.case}`);
  }
}

function mapUnsupportedMessagePayload(_: UnsupportedPayload): IUnsupportedPayload {
  return {
    payloadType: PayloadType.Unsupported,
  };
}

function mapIMessagePayload(payload: MessagePayload): IMessagePayload {
  switch (payload.type.case) {
    case 'album':
      return mapAlbumMessagePayload(payload.type.value);
    case 'audio':
      return mapAudioMessagePayload(payload.type.value);
    case 'file':
      return mapFileMessagePayload(payload.type.value);
    case 'image':
      return mapImageMessagePayload(payload.type.value);
    case 'sticker':
      return mapStickerMessagePayload(payload.type.value);
    case 'text':
      return mapTextMessagePayload(payload.type.value);
    case 'e2ee':
      return mapE2eeMessagePayload(payload.type.value);
    case 'video':
      return mapVideoMessagePayload(payload.type.value);
    case 'voice':
      return mapVoiceMessagePayload(payload.type.value);
    case 'orderedItem':
      return mapOrderedItemsMessagePayload(payload.type.value);
    case 'systemMessage':
      return mapSystemMessagePayload(payload.type.value);
    case 'unsupported':
      return mapUnsupportedMessagePayload(payload.type.value);
    default:
      throw new Error(`Unknown message payload: ${payload.type.case}`);
  }
}

export function mapIForwardMessage(forward: ForwardedSource, groupId?: string): IForwardMessage {
  return {
    from: forward.title,
    groupId: forward.chatId ? String(forward.chatId) : undefined,
    messageId: String(forward.messageId),
    senderId: String(forward.senderId),
    clientGroupSourceId: groupId || undefined,
  };
}

export function mapIMessage(message: Message): IMessage {
  if (!message.payload) {
    throw new Error(`Payload doesn't exist in message with id: ${message.id}`);
  }

  return {
    id: message.id.toString(),
    groupId: message.chatId.toString(),
    clientTime: message.clientTs.toString(),
    clientUuid: message.clientUuid,
    serverTime: message.serverTs.toString(),
    editTime: message.editTs?.toString(),
    senderId: message.senderId.toString(),
    deepLink: message.deepLink,
    views: message.viewsCount.toString(),
    threadId: undefined,
    links: message.links,
    forwardedMessage: message.forwardedSource
      ? mapIForwardMessage(message.forwardedSource, message.chatId.toString())
      : undefined,
    repliedMessage: message.quotedMessageData
      ? mapIQuotedMessageToIRepliedMessage(message.quotedMessageData)
      : undefined,
    quote: message.quotedMessageData
      ? mapIQuotedMessageToIQuote(message.quotedMessageData)
      : undefined,
    usersReactions: {
      currentUserReactionId: message.reactions?.currentUserReaction
        ? message.reactions.currentUserReaction.toString()
        : undefined,
      usersReactions: (message.reactions?.usersReactions || []).reduce((result, item) => {
        result[item.reactionId.toString()] = Number(item.reactionsCount);
        return result;
      }, {} as Record<string, number>),
    },
    payload: mapIMessagePayload(message.payload),
  };
}

export function mapIQuotedMessageDataUserSource(value?: Source_User): IQuoteUserSource {
  return { userId: value?.userId.toString() || '' };
}

export function mapIQuotedMessageDataChatSource(value?: Source_Chat): IQuoteChatSource {
  return {
    chatId: value?.chatId.toString() || '',
    messageId: value?.messageId.toString() || '',
    title: value?.title || '',
  };
}

export function mapIQuotedMessageDataUserChatSource(value: Source_UserChat): IQuoteUserChatSource {
  return {
    user: mapIQuotedMessageDataUserSource(value.user),
    chat: mapIQuotedMessageDataChatSource(value.chat),
  };
}

export function mapIQuotedMessageDataUnknownSource(value?: Source_Unknown): IQuoteUnknownSource {
  return { title: value?.title || '' };
}

export function mapIQuotedMessageDataSource(source?: Source): TQuoteSource | null {
  if (!source) {
    return null;
  }
  switch (source.source.case) {
    case 'userChat':
      return { userChat: mapIQuotedMessageDataUserChatSource(source.source.value) };
    case 'user':
      return { user: mapIQuotedMessageDataUserSource(source.source.value) };
    case 'chat':
      return { chat: mapIQuotedMessageDataChatSource(source.source.value) };
    case 'unknown':
      return { unknown: mapIQuotedMessageDataUnknownSource(source.source.value) };
    default:
      return null;
  }
}

export function mapIQuotedMessageToIRepliedMessage(
  quotedMessageData: QuotedMessageData
): IRepliedMessageData | undefined {
  const replyData =
    quotedMessageData.type.case === 'reply' && quotedMessageData.type.value
      ? (quotedMessageData.type.value as unknown as QuotedMessageData_Reply)
      : undefined;

  const source = mapIQuotedMessageDataSource(quotedMessageData.source);
  const chat = source?.userChat?.chat || source?.chat;
  const user = source?.userChat?.user || source?.user;

  if (!replyData || !source || !chat) {
    return undefined;
  }

  if (!replyData.payload) {
    throw new Error(`Payload doesn't exist in replied message with id: ${chat?.messageId}`);
  }

  return {
    id: chat.messageId || '',
    senderId: user?.userId || '',
    sender: '',
    groupId: String(chat.chatId),
    payload: mapIMessagePayload(replyData.payload),
  };
}

export function mapIQuotedMessageToIQuote(
  quotedMessageData: QuotedMessageData
): IQuote | undefined {
  const quoteTextData =
    quotedMessageData.type.case === 'text' && quotedMessageData.type.value
      ? (quotedMessageData.type.value as unknown as QuotedMessageData_Text)
      : undefined;

  const source = mapIQuotedMessageDataSource(quotedMessageData.source);

  if (!quoteTextData?.text || !source) {
    return undefined;
  }

  return {
    text: quoteTextData.text,
    source,
  };
}

export function mapToSendMessagePayloadData(
  payload: IUpdateMessageRequestPayloadData['payload']
): SendMessagePayloadData {
  switch (payload.payloadType) {
    case PayloadType.Album:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'album',
          value: mapToSendMessageAlbumPayload(payload as IAlbumRequestData),
        },
      });
    case PayloadType.AudioMessage:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'audio',
          value: mapToSendMessageAudioPayload(payload as IAudioRequestData),
        },
      });
    case PayloadType.File:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'file',
          value: mapToSendMessageFilePayload(payload as IFileRequestData),
        },
      });
    case PayloadType.Image:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'image',
          value: mapToSendMessageImagePayload(payload as IImageRequestData),
        },
      });
    case PayloadType.Sticker:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'sticker',
          value: mapToSendMessageStickerPayload(payload as IStickerRequestData),
        },
      });
    case PayloadType.RichText:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'text',
          value: mapToTextMessagePayload(payload as ITextRequestData),
        },
      });
    case PayloadType.E2ee:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'e2ee',
          value: mapToE2eeMessagePayload(payload as IE2eeRequestData),
        },
      });
    case PayloadType.Video:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'video',
          value: mapToSendMessageVideoPayload(payload as IVideoRequestData),
        },
      });
    case PayloadType.VoiceMessage:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'voice',
          value: mapToSendMessageVoicePayload(payload as IVoiceRequestData),
        },
      });
    case PayloadType.ContainerOrdered:
      return create(SendMessagePayloadDataSchema, {
        payload: {
          case: 'orderedItem',
          value: mapToSendMessageOrderedItemsMessagePayload(payload as IOrderedItemRequestData),
        },
      });
    default:
      throw new Error(`Unknown payload type: ${payload.payloadType}`);
  }
}

export function mapToSendMessageQuotedMessageData(
  quotedMessageData?: IMessageRequestQuotedMessageData
): QuotedMessageRequestData | undefined {
  if (!quotedMessageData) {
    return undefined;
  }
  return create(QuotedMessageRequestDataSchema, {
    text: quotedMessageData.text,
    sourceMessageId: BigInt(quotedMessageData.sourceMessageId),
    sourceChatId: BigInt(quotedMessageData.sourceChatId),
  });
}

function mapToSendMessageAlbumPayload(
  payload: IAlbumRequestData
): SendMessagePayloadData_AlbumData {
  return create(SendMessagePayloadData_AlbumDataSchema, {
    comment: payload.comment,
    payloads: payload.payloads.map(mapToSendMessageOrderedItemsMessagePayload),
  });
}

function mapDurationToBigInt(duration: string | number): bigint {
  const durationNumber = typeof duration === 'string' ? parseFloat(duration) : duration;
  return BigInt(Math.round(durationNumber));
}

function mapToSendMessageAudioPayload(
  payload: IAudioRequestData
): SendMessagePayloadData_AudioData {
  return create(SendMessagePayloadData_AudioDataSchema, {
    duration: mapDurationToBigInt(payload.duration),
    comment: payload.comment,
    title: payload.title,
    artist: payload.artist,
    url: payload.url,
  });
}

function mapToSendMessageFilePayload(payload: IFileRequestData): SendMessagePayloadData_FileData {
  return create(SendMessagePayloadData_FileDataSchema, {
    url: payload.url,
    comment: payload.comment,
  });
}

function mapToSendMessageImagePayload(
  payload: IImageRequestData
): SendMessagePayloadData_ImageData {
  return create(SendMessagePayloadData_ImageDataSchema, {
    url: payload.url,
    comment: payload.comment,
  });
}

function mapToSendMessageStickerPayload(
  payload: IStickerRequestData
): SendMessagePayloadData_StickerData {
  return create(SendMessagePayloadData_StickerDataSchema, {
    id: BigInt(payload.id),
    stickerPackId: payload.stickerPackId ? BigInt(payload.stickerPackId) : undefined,
  });
}

function mapToTextMessagePayload(payload: ITextRequestData): SendMessagePayloadData_TextData {
  return create(SendMessagePayloadData_TextDataSchema, {
    text: payload.text,
    linkPreview: payload.linkPreview ? mapToLinkPreview(payload.linkPreview) : undefined,
    linkPreviewCancelled: payload.linkPreviewCancelled,
  });
}

function mapToE2eeMessagePayload(payload: IE2eeRequestData): SendMessagePayloadData_E2eeData {
  return create(SendMessagePayloadData_E2eeDataSchema, {
    epoch: BigInt(payload.epoch),
    senderDeviceId: payload.senderDeviceId,
    payload: new Uint8Array(payload.e2eePayload),
    selfPayload: new Uint8Array(payload.selfPayload),
  });
}

function mapToSendMessageVideoPayload(
  payload: IVideoRequestData
): SendMessagePayloadData_VideoData {
  return create(SendMessagePayloadData_VideoDataSchema, {
    comment: payload.comment,
    url: payload.url,
    previewUrl: payload.previewUrl,
    graphicSize: payload.graphicSize ? mapToGraphicSize(payload.graphicSize) : undefined,
  });
}

function mapToSendMessageVoicePayload(
  payload: IVoiceRequestData
): SendMessagePayloadData_VoiceData {
  return create(SendMessagePayloadData_VoiceDataSchema, {
    comment: payload.comment,
    duration: mapDurationToBigInt(payload.duration),
    histogram: payload.histogram,
    url: payload.url,
  });
}

function mapToSendMessageOrderedItemsMessagePayload(
  payload: IOrderedItemRequestData
): SendMessagePayloadData_OrderedItemData {
  return create(SendMessagePayloadData_OrderedItemDataSchema, {
    id: payload.id,
    order: payload.order,
    payload: mapToSendMessagePayloadData(payload.payload),
  });
}

const mediaTypeMap: Record<Message_MediaType, MediaType> = {
  [Message_MediaType.AudioMessage]: MediaType.Audio,
  [Message_MediaType.Album]: MediaType.Album,
  [Message_MediaType.Article]: MediaType.Article,
  [Message_MediaType.Link]: MediaType.Link,
  [Message_MediaType.File]: MediaType.File,
  [Message_MediaType.Image]: MediaType.Image,
  [Message_MediaType.Video]: MediaType.Video,
  [Message_MediaType.VoiceMessage]: MediaType.Voice,
};

export function mapMediaType(type: Message_MediaType): MediaType {
  return mediaTypeMap[type];
}

const messageMediaTypeMap: Record<MediaType, Message_MediaType> = {
  [MediaType.Audio]: Message_MediaType.AudioMessage,
  [MediaType.Album]: Message_MediaType.Album,
  [MediaType.Article]: Message_MediaType.Article,
  [MediaType.Link]: Message_MediaType.Link,
  [MediaType.File]: Message_MediaType.File,
  [MediaType.Image]: Message_MediaType.Image,
  [MediaType.Video]: Message_MediaType.Video,
  [MediaType.Voice]: Message_MediaType.VoiceMessage,
};

export function mapMessageMediaType(type: MediaType): Message_MediaType {
  return messageMediaTypeMap[type];
}

const messageDirectionMap: Record<GetMessagesDirection, Message_Direction> = {
  [GetMessagesDirection.Newest]: Message_Direction.NEW,
  [GetMessagesDirection.Oldest]: Message_Direction.OLD,
};

export function mapMessageDirection(direction: GetMessagesDirection): Message_Direction {
  return messageDirectionMap[direction];
}

const messageDeleteModeMap: Record<DeleteMessagesMode, Message_DeleteMode> = {
  [DeleteMessagesMode.All]: Message_DeleteMode.FOR_ALL,
  [DeleteMessagesMode.Me]: Message_DeleteMode.FOR_ME,
};

export function mapMessageDeleteMode(mode: DeleteMessagesMode): Message_DeleteMode {
  return messageDeleteModeMap[mode];
}

export function mapIForwardMessagesResponse(
  response: ForwardMessagesResponse
): IForwardMessagesResponse {
  if (response.type.case === 'success') {
    return {
      forwardedMessages: response.type.value.items.map((item) => ({
        groupId: item.chatId.toString(),
        lastMessageTs: '',
        messages: item.items.map(mapIMessage),
      })),
      unavailableGroups: response.type.value.unavailableChatIds.map((id) => id.toString()),
      unavailableContacts: response.type.value.unavailableContactIds.map((id) => id.toString()),
    };
  } else {
    return {
      forwardedMessages: [],
      unavailableGroups: [],
      unavailableContacts: [],
    };
  }
}

export function mapMessagesCountsResponse(
  data: MessageCountData[]
): IGetMessagesByMediaTypesCountResponse {
  const response: IGetMessagesByMediaTypesCountResponse = {
    [MediaType.Album]: 0,
    [MediaType.Article]: 0,
    [MediaType.Audio]: 0,
    [MediaType.File]: 0,
    [MediaType.Image]: 0,
    [MediaType.Link]: 0,
    [MediaType.Video]: 0,
    [MediaType.Voice]: 0,
  };

  data.forEach((item): void => {
    response[mapMediaType(item.type)] = Number(item.counts);
  });

  return response;
}

export function mapSaveScheduledMessageRequest(
  data: ISendScheduledMessageRequest
): ScheduleMessageRequest {
  return create(ScheduleMessageRequestSchema, {
    chatId: BigInt(data.groupId),
    delayMs: BigInt(data.delay),
    payload: mapToSendMessagePayloadData(data.payload),
    clientUuid: data.clientUuid,
    clientTs: BigInt(data.clientTime),
    quotedMessageData: mapToSendMessageQuotedMessageData(data.quotedMessageData),
  });
}

export function mapUpdateScheduledMessageRequest(
  data: IUpdateScheduledMessageRequest
): UpdateScheduledMessageRequest {
  return create(UpdateScheduledMessageRequestSchema, {
    chatId: BigInt(data.groupId),
    messageId: BigInt(data.messageId),
    clientTs: BigInt(data.clientTime),
    delayMs: BigInt(data.delay),
    payload: mapToSendMessagePayloadData(data.payload),
    quotedMessageData: mapToSendMessageQuotedMessageData(data.quotedMessageData),
  });
}

export function mapScheduledMessageToIScheduledMessage(
  message: ScheduledMessage
): IScheduledMessage {
  return {
    payload: mapIMessagePayload(message.payload!),
    id: String(message.id),
    groupId: String(message.chatId),
    clientUuid: message.clientUuid,
    delay: String(message.delayMs),
    serverTime: String(message.serverTs),
    clientTime: String(message.clientTs),
    status: message.status,
    repliedMessage: message.quotedMessageData
      ? mapIQuotedMessageToIRepliedMessage(message.quotedMessageData)
      : undefined,
  };
}

export function mapScheduledMessagesResponse(
  data: GetScheduledMessagesSuccess
): IGetScheduledMessagesResponse {
  return {
    nextCursor: data.nextCursor,
    messages: data.scheduledMessages.map(mapScheduledMessageToIScheduledMessage),
  };
}
